import { ArrowLeft, Home } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface NavBarProps {
  title?: string;
  showBack?: boolean;
  showHome?: boolean;
  showMenu?: boolean;
  onMenuClick?: () => void;
}

export function NavBar({
  title,
  showBack = false,
  showHome = false,
  showMenu = false,
  onMenuClick
}: NavBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { isAuthenticated, logout, user } = useAuth();

  // Auto-detect title based on pathname if not provided
  const getTitle = () => {
    if (title) return title;
    
    if (!pathname) return 'Findr';
    
    // Handle nested routes
    if (pathname.startsWith('/chat/')) {
      return 'Chat'; // Will be replaced with actual company name from data later
    }
    
    switch (pathname) {
      case '/':
        return 'Findr';
      case '/swipe':
        return 'Swipe';
      case '/matches':
        return 'Matcher';
      case '/chat':
        return 'Meldinger';
      case '/profile':
        return 'Profil';
      case '/candidates':
        return 'Kandidater';
      default:
        return 'Findr';
    }
  };

  // Auto-detect if we should show back button (only on nested routes like /chat/[id])
  const mainRoutes = ['/', '/swipe', '/matches', '/chat', '/profile', '/candidates'];
  const isNestedRoute = pathname && !mainRoutes.includes(pathname) && pathname.startsWith('/');
  const shouldShowBack = showBack || isNestedRoute;

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  // Don't show NavBar on auth page or when not authenticated (landing page)
  if (pathname === '/auth' || !isAuthenticated) {
    return null;
  }

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-[var(--blur-backdrop)] border-b safe-area-top"
      style={{ 
        backgroundColor: 'var(--color-bg-primary)',
        borderColor: 'var(--color-border)',
        height: '56px'
      }}
    >
      <div className="flex items-center justify-between h-14 px-4">
        {shouldShowBack ? (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-10 h-10 -ml-2 rounded-full transition-all duration-[var(--motion-duration-fast)] active:scale-95"
            style={{
              color: 'var(--color-text-primary)',
            }}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
        ) : showHome ? (
          <Link
            to="/"
            className="flex items-center justify-center w-10 h-10 -ml-2 rounded-full transition-all duration-[var(--motion-duration-fast)] active:scale-95"
            style={{
              color: 'var(--color-text-primary)',
            }}
          >
            <Home className="w-6 h-6" />
          </Link>
        ) : (
          <div className="w-10" />
        )}

        <h1 
          className="font-semibold"
          style={{ 
            fontSize: 'var(--font-size-body-lg)', 
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-primary)'
          }}
        >
          {getTitle()}
        </h1>

        <div className="w-10" />
      </div>
    </header>
  );
}
